import { usePageVisible } from '@umu/umu-react-hooks';
import umuWebview from '@umu/umu-webview';
import { useMemoizedFn } from 'ahooks';
import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { IStaticInfo } from '../components/searchPdfToolBar/interface';
import { configRenderHelper } from '../services/configRenderHelper';
import { type IPdfReaderProps, type PageFlipMode, RENDER_TYPE } from '../types';
import { getPixelRatio } from '../utils/common';
import { ensureElementVisible, getScrollContainer } from '../utils/ensureVisibleInContainer';
import { usePdfSearch } from './usePDFSearch';
import { usePdfLoad } from './usePdfLoad';
import { usePdfOutline } from './usePdfOutline';
import { usePdfPagination } from './usePdfPagination';
import { usePdfReaderState } from './usePdfReaderState';
import { usePdfViewport } from './usePdfViewport';
import { usePinchZoom } from './usePinchZoom';

/** 轮询直到搜索高亮元素进入视口（从 onJumpToElement 提取以降低认知复杂度） */
function tryScrollHighlightIntoView(
    containerEl: HTMLDivElement,
    startAt: number,
    maxMs: number,
    margin: number,
    screenState: string | undefined
): void {
    const now = performance.now();
    const el = containerEl.querySelector('.pdf-content-text-search-highlight.selected') as HTMLElement | null;
    if (!el) {
        if (now - startAt < maxMs) {
            requestAnimationFrame(() => tryScrollHighlightIntoView(containerEl, startAt, maxMs, margin, screenState));
        }
        return;
    }
    const scrollContainer = getScrollContainer(containerEl, screenState ?? '');
    if (!scrollContainer) return;
    const siblingsNav = containerEl.querySelector('.siblings-nav') as HTMLElement | null;
    const reservedBottom =
        screenState !== 'full' ? (siblingsNav ? Math.round(siblingsNav.getBoundingClientRect().height) : 54) : 0;
    ensureElementVisible(scrollContainer, el, margin, { reservedBottom });
}

/**
 * PDF 阅读器主控制器 Hook
 *
 * 核心职责：
 * 1. 聚合所有子 Hook（加载、分页、视口、搜索、大纲）
 * 2. 管理全局 UI 状态（全屏、工具栏、大纲显示等）
 * 3. 处理渲染类型配置（根据配置决定使用 Canvas/SVG/Image）
 * 4. 管理全屏逻辑和页面可见性
 * 5. 提供统一的控制器接口给 UI 组件
 */
const defaultRenderPixelRadio = getPixelRatio();
export function usePdfReader(props: IPdfReaderProps) {
    const { screenState: initialScreenState = 'no-full', screenCallback, pageChangeCallback, initPageNum = 1 } = props;

    // 使用 ref 持有最新的 screenCallback，避免 useEffect 依赖不稳定引用导致重复执行
    const screenCallbackRef = useRef(screenCallback);
    screenCallbackRef.current = screenCallback;

    // ========== UI 状态管理（useReducer，支持批量更新避免多次 re-render）==========
    const { state: uiState, dispatch } = usePdfReaderState({ screenState: initialScreenState });
    const {
        screenState,
        isShowBar,
        showOutlines,
        pageFlipMode,
        renderType,
        isManualScrolling,
        isMobileShowSearchDrawer,
    } = uiState;
    // 渲染像素比和配置就绪标记不在 useReducer 内（与业务配置加载流程耦合，保持独立）
    const [renderPixelRadio, setRenderPixelRadio] = useState<number>(defaultRenderPixelRadio);
    const [renderTypeReady, setRenderTypeReady] = useState(false);
    // ========== Refs ==========
    const containerRef = useRef<HTMLDivElement>(null); // 主容器引用
    // containerReady：容器已挂载到 DOM，确保 usePinchZoom 等依赖 DOM 的 hook 在 ref 就绪后才绑定事件
    const [containerReady, setContainerReady] = useState(false);
    const setContainerRef = useMemoizedFn((el: HTMLDivElement | null) => {
        (containerRef as MutableRefObject<HTMLDivElement | null>).current = el;
        setContainerReady(!!el);
    });
    const scrollResetTimerRef = useRef<NodeJS.Timeout | null>(null);
    // 为外部消费者提供稳定的 setter 接口（与原 useState setter 签名兼容）
    const setIsMobileShowSearchDrawer = useMemoizedFn((v: boolean) => {
        dispatch({ type: 'SET_MOBILE_SEARCH_DRAWER', payload: v });
    });

    // ========== 子 Hook 聚合 ==========

    // 1. PDF 加载：负责加载 PDF 文档
    const { pdfDoc, loading, error, totalPages, pinchZoomEnabled, enableCjkFix } = usePdfLoad(props);

    // 2. 分页管理：负责页码跳转和边界检查
    const {
        pageNum,
        jumpTo: originalJumpTo,
        setPageNumSilent,
        nextPage: baseNextPage,
        prevPage: basePrevPage,
    } = usePdfPagination({
        totalPages,
        initialPage: (() => {
            const num = Number(initPageNum);
            // 处理 NaN 情况，确保初始页码有效
            const validPage = Number.isFinite(num) && !Number.isNaN(num) ? num : 1;
            return validPage;
        })(),
        onChange: (page, total) => {
            pageChangeCallback?.(page, total);
        },
    });

    /**
     * 统一的手动跳转状态管理
     */
    const startManualScroll = useMemoizedFn(() => {
        // BATCH_UPDATE：一次 dispatch 同时更新两个状态，避免两次 re-render
        dispatch({ type: 'BATCH_UPDATE', payload: { isManualScrolling: true, isShowBar: true } });
        if (scrollResetTimerRef.current) {
            clearTimeout(scrollResetTimerRef.current);
        }
        scrollResetTimerRef.current = setTimeout(() => {
            dispatch({ type: 'SET_IS_MANUAL_SCROLLING', payload: false });
            scrollResetTimerRef.current = null;
        }, 1500); // 增加到 1500ms，确保在所有设备上自动/手动平滑滚动完全结束
    });

    /**
     * 增强版跳转函数
     */
    const jumpTo = useMemoizedFn((page: number) => {
        startManualScroll();
        originalJumpTo(page);
    });

    const nextPage = useMemoizedFn(() => {
        startManualScroll();
        baseNextPage();
    });

    const prevPage = useMemoizedFn(() => {
        startManualScroll();
        basePrevPage();
    });

    useEffect(() => {
        return () => {
            if (scrollResetTimerRef.current) {
                clearTimeout(scrollResetTimerRef.current);
            }
        };
    }, []);

    // 3. 视口管理：负责缩放比例计算和控制
    const {
        scale,
        renderScale,
        initScale,
        setScale,
        applyScale,
        zoomIn: baseZoomIn,
        zoomOut,
        zoomReset,
    } = usePdfViewport({
        pdfDoc,
        pageNum,
        containerRef,
        screenState,
    });

    /**
     * 放大时标记手动滚动，防止缩放引起的 DOM 重排触发滚动事件误隐藏工具栏
     */
    const zoomIn = useMemoizedFn(() => {
        startManualScroll();
        baseZoomIn();
    });

    // 3a. 双指捏合缩放（containerReady 确保 ref 已挂载，pinchZoomEnabled 由 API 控制开关）
    // 非全屏模式使用 setScale（始终同步更新 renderScale，避免因 CSS transform + top-center 原点
    // 导致页面左侧内容溢出到负坐标区域而无法滚动到）；
    // 全屏模式使用 applyScale（Tier 机制，减少多页虚拟列表的重绘次数）。
    const pinchApplyScale = useMemoizedFn((newScale: number) => {
        if (screenState === 'full') {
            applyScale(newScale);
        } else {
            setScale(newScale);
        }
    });
    usePinchZoom({
        containerRef,
        scale,
        applyScale: pinchApplyScale,
        enabled: pinchZoomEnabled && containerReady,
    });

    // Canvas 模式下使用 renderScale（阈值驱动），其他模式直接跟随视觉 scale（SVG/Image 无需优化）
    const effectiveRenderScale = renderType === RENDER_TYPE.CANVAS ? renderScale || scale : scale;

    // 4. 搜索功能：负责文本搜索和高亮
    // 文本层坐标必须与 canvas 渲染 scale 对齐，否则高亮位置会偏移
    const { onSearch, registerTextLayer, unregisterTextLayer, updateVisiblePages, setActiveMatch } = usePdfSearch({
        pdfDoc,
        totalPages,
        scale: effectiveRenderScale,
        currentPage: pageNum,
        renderPixelRadio, // 传递 renderPixelRadio，确保文本层与 Canvas 使用相同的 pixelRatio
    });

    // 5. 大纲提取：负责提取 PDF 目录结构
    const outlines = usePdfOutline(pdfDoc);

    // ========== 渲染类型配置 ==========
    /**
     * 根据配置决定渲染类型和像素比
     *
     * 配置来源：
     * - resourceUrl: 资源 URL
     * - elementId: 元素 ID
     * - enterprise_id: 企业 ID
     * - pdfImages: PDF 图片列表
     *
     * 渲染类型优先级：根据配置白名单决定使用 Canvas/SVG/Image
     *
     * 注意：需要等待配置加载完成后再使用配置，避免配置未加载时使用空配置
     */
    const updateRenderConfig = () => {
        const { resourceUrl, elementId, userInfo, pdfImages } = props;
        const newRenderType = configRenderHelper.getRenderType(
            resourceUrl || '',
            elementId || '',
            userInfo ? userInfo.enterprise_id : '',
            pdfImages
        );
        const newRenderPixelRadio = configRenderHelper.getRenderPixelRadio(
            resourceUrl || '',
            elementId || '',
            userInfo ? userInfo.enterprise_id : ''
        );
        dispatch({ type: 'SET_RENDER_TYPE', payload: newRenderType });
        newRenderPixelRadio && setRenderPixelRadio(newRenderPixelRadio);
        setRenderTypeReady(true); // 标记配置完成
    };
    useEffect(() => {
        if (configRenderHelper.isLoaded) {
            // 配置已加载过（如再次打开详情），直接应用配置，否则 renderTypeReady 一直为 false，内容区不渲染
            updateRenderConfig();
        } else {
            configRenderHelper.onConfigLoaded(() => {
                updateRenderConfig();
            });
            configRenderHelper.loadConfig();
        }
    }, []);

    // 7. Fullscreen Logic
    usePageVisible((status) => {
        if (status === 'hidden') {
            umuWebview.api.exitFullscreen();
        }
        if (status === 'visible' && screenState === 'full') {
            umuWebview.api.requestFullscreen();
        }
        screenCallbackRef.current?.(screenState === 'full');
    });

    useEffect(() => {
        if (screenState === 'full') {
            umuWebview.api.requestFullscreen();
            startManualScroll();
        } else {
            umuWebview.api.exitFullscreen();
        }
        screenCallbackRef.current?.(screenState === 'full');
    }, [screenState, startManualScroll]);

    // Handlers
    const toggleFullScreen = useMemoizedFn(() => {
        const isCurrentFull = screenState === 'full';
        const nextState = isCurrentFull ? 'no-full' : 'full';
        dispatch({ type: 'SET_SCREEN_STATE', payload: nextState });
        // 同步调用 screenCallback，确保父组件状态与内部状态在同一批次更新，
        // 避免 useEffect 异步执行导致的渲染间隙（部分浏览器中 DetailHeard 不隐藏）
        screenCallbackRef.current?.(nextState === 'full');
    });

    const toggleOutline = useMemoizedFn(() => {
        dispatch({ type: 'TOGGLE_SHOW_OUTLINES' });
    });

    const closeOutline = useMemoizedFn(() => {
        dispatch({ type: 'SET_SHOW_OUTLINES', payload: false });
    });

    const changeFlipMode = useMemoizedFn((mode: PageFlipMode) => {
        dispatch({ type: 'SET_PAGE_FLIP_MODE', payload: mode });
        jumpTo(pageNum);
    });

    /**
     * 关闭搜索框时的处理
     *
     * 注意：关闭搜索框时不自动清空搜索内容和高亮
     * 清空逻辑由输入框为空时触发搜索函数来处理
     */
    const onSearchClose = useMemoizedFn(() => {
        // 关闭时不自动清空，保持搜索状态
        // 如果用户需要清空，可以通过清空输入框来触发
    });

    const onJumpToElement = useMemoizedFn(async (element: IStaticInfo) => {
        if (element?.page) {
            setActiveMatch?.(element);
            if (element.page !== pageNum) {
                jumpTo(element.page);
            }
            const container = containerRef.current;
            if (container) {
                const startAt = performance.now();
                requestAnimationFrame(() => tryScrollHighlightIntoView(container, startAt, 1600, 24, screenState));
            }
            return true;
        }
        return false;
    });

    /**
     * 工具栏显示/隐藏控制
     *
     * 核心逻辑：
     * 1. showToolbar: 显示工具栏（设置为 true）
     * 2. hideToolbar: 隐藏工具栏（非全屏模式保持显示，全屏模式可隐藏）
     * 3. toggleToolbar: 切换工具栏显示状态（非全屏模式保持显示，全屏模式可切换）
     */
    const showToolbar = useMemoizedFn(() => {
        dispatch({ type: 'SET_IS_SHOW_BAR', payload: true });
    });

    const hideToolbar = useMemoizedFn(() => {
        // 非全屏模式下 payload=true（保持显示），全屏下 payload=false（隐藏）
        dispatch({ type: 'SET_IS_SHOW_BAR', payload: screenState !== 'full' });
    });

    const toggleToolbar = useMemoizedFn(() => {
        // 非全屏模式下，工具栏始终显示；全屏下切换
        if (screenState !== 'full') {
            dispatch({ type: 'SET_IS_SHOW_BAR', payload: true });
            return;
        }
        dispatch({ type: 'TOGGLE_SHOW_BAR' });
    });

    // Sync external props if needed
    useEffect(() => {
        if (props.screenState && props.screenState !== screenState) {
            dispatch({ type: 'SET_SCREEN_STATE', payload: props.screenState });
        }
    }, [props.screenState]);

    // ========== Toolbar Props 聚合 ==========
    // 将工具栏相关的 props 聚合到一个对象中，简化组件传参
    const toolbarProps = {
        currentPage: pageNum,
        totalPages,
        scale,
        screenState,
        isShowBar,
        loading,
        allowSearch: props.allowSearch,
        pageFlipMode,
        showOutlines,
        isMobileShowSearchDrawer,
        setIsMobileShowSearchDrawer,
        onZoomIn: zoomIn,
        onZoomOut: zoomOut,
        onPageJump: jumpTo,
        onFullScreenToggle: toggleFullScreen,
        onFlipModeChange: changeFlipMode,
        onSearch,
        onSearchClose,
        onJumpToElement,
        showToolbar,
        hideToolbar,
    };

    // 确保 pageNum 是有效值
    const validPageNum = Number.isFinite(pageNum) && !Number.isNaN(pageNum) ? pageNum : 1;

    return {
        state: {
            pdfDoc,
            loading,
            error,
            totalPages,
            pageNum: { value: validPageNum }, // Convert to object format to match IPdfReaderState
            scale,
            renderScale: effectiveRenderScale, // canvas 渲染 scale（Canvas 模式下可能与 scale 不同）
            initScale, // 初始适配 scale，用于目录缩略图（不受用户缩放影响）
            screenState,
            isShowBar,
            showOutlines,
            pageFlipMode,
            outlines,
            renderType,
            renderPixelRadio,
            renderTypeReady, // 渲染类型配置是否完成
            pdfImages: props.pdfImages, // 传递 pdfImages 到 state
            enableCjkFix,
        },
        controller: {
            jumpTo,
            setPageNumSilent,
            nextPage,
            prevPage,
            zoomIn,
            zoomOut,
            zoomReset,
            toggleFullScreen,
            toggleOutline,
            closeOutline,
            changeFlipMode,
            onSearch,
            onSearchClose,
            onJumpToElement,
            registerTextLayer,
            unregisterTextLayer,
            updateVisiblePages,
            showToolbar,
            hideToolbar,
            toggleToolbar,
        },
        refs: {
            containerRef,
            setContainerRef,
        },
        isMobileShowSearchDrawer,
        setIsMobileShowSearchDrawer,
        toolbarProps, // Grouped props
        isManualScrolling,
    };
}
